import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ScopingAILayout from '../../components/RevisrLayout';
import { Stepper } from '@foundation/components/stepper';
import { Button } from '@foundation/components/ui/button';
import { Input } from '@foundation/components/ui/input';
import { Textarea } from '@foundation/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@foundation/components/ui/card';
import { Badge } from '@foundation/components/ui/badge';
import { useUser } from '@foundation/contextapi/UserContext';
import { useWorkspaceContext } from '@foundation/hooks/useWorkspaceContext';
import { useToast } from '@foundation/hooks/use-toast';
import TemplateSelector from '../../components/create-policy/TemplateSelector';
import OrgContextCard from '../../components/create-policy/OrgContextCard';
import PerfectMarkdownRenderer from '../../components/common/PerfectMarkdownRenderer';
import { createPolicyRecord } from '../../services/policyGenerationService';
import { adminLibraryService } from '../../services/adminLibraryService';
import type { PolicyGenerationTemplate } from '../../types/policyGeneration';
import type { AdminLibraryItem } from '../../types/adminLibrary';
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  FileText,
  Loader2,
  Lock,
} from 'lucide-react';
import { useSubscriptionUsage } from '../../hooks/useSubscriptionUsage';

const CREATE_POLICY_STEPS = [
  { label: 'Details', description: 'Enter policy info' },
  { label: 'Generate', description: 'AI generates draft' },
  { label: 'Edit', description: 'Review & refine' },
  { label: 'Export', description: 'Preview & export' },
];

export default function PolicyDetailsPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { currentWorkspace } = useWorkspaceContext();
  const { toast } = useToast();

  const orgId = currentWorkspace?.id || '';
  const userId = user?.id || '';

  const { canCreatePolicy, policyUsage, isLoading: usageLoading } = useSubscriptionUsage();

  // Form state
  const [policyName, setPolicyName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<PolicyGenerationTemplate | null>(null);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [policyLimit, setPolicyLimit] = useState<{ current?: number; limit?: number } | null>(null);

  // Pre-populate limit state from usage data on load
  useEffect(() => {
    if (!usageLoading && !canCreatePolicy && policyUsage) {
      setPolicyLimit({ current: policyUsage.current, limit: policyUsage.limit });
    }
  }, [usageLoading, canCreatePolicy, policyUsage]);

  // Template preview content (mock for now - actual template markdown could come from API)
  const [templatePreview] = useState<string | null>(null);

  // Templates — fetched using the same logic as the Revisr Library so visibility
  // rules and doc IDs are consistent with what the generate endpoint expects.
  const [templates, setTemplates] = useState<PolicyGenerationTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  useEffect(() => {
    if (!user?.id) return;

    const fetchTemplates = async () => {
      try {
        setIsLoadingTemplates(true);
        const response = await adminLibraryService.listLibraryItems({
          status: 'published',
          limit: 200,
        });
        const mapped: PolicyGenerationTemplate[] = response.items.map(
          (item: AdminLibraryItem & { chunks_count?: number }) => ({
            id: item.id,
            doc_name: item.title,
            category: (item.metadata?.category as string) || 'standard',
            file_type: item.file_type,
            file_size: item.file_size,
            chunk_count: item.chunks_count ?? 0,
            metadata: item.metadata,
          })
        );
        setTemplates(mapped);
      } catch (err) {
        console.error('Error loading templates:', err);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [user?.id]);

  const isFormValid = policyName.trim().length > 0 && selectedTemplate !== null;

  const handleTemplateChange = useCallback((template: PolicyGenerationTemplate | null) => {
    setSelectedTemplate(template);
  }, []);

  const handleSubmit = async () => {
    if (!isFormValid || !selectedTemplate) return;

    try {
      setIsSubmitting(true);

      // Create policy generation record
      const result = await createPolicyRecord({
        policy_name: policyName.trim(),
        template_store_id: selectedTemplate.id,
        generation_prompt: additionalInstructions.trim() || undefined,
        org_id: orgId,
        user_id: userId,
      });

      // Store workflow data in sessionStorage
      sessionStorage.setItem('create_policy_policy_review_id', result.id);
      sessionStorage.setItem('create_policy_template_id', selectedTemplate.id);
      sessionStorage.setItem('create_policy_template_name', selectedTemplate.doc_name);
      sessionStorage.setItem('create_policy_policy_name', policyName.trim());
      if (additionalInstructions.trim()) {
        sessionStorage.setItem('create_policy_additional_prompt', additionalInstructions.trim());
      }

      // Navigate to generate page with policy ID in URL
      navigate(`/revisr/create-policy/generated?id=${result.id}`);
    } catch (error) {
      console.error('Error creating policy record:', error);
      if ((error as any).isLimitReached) {
        setPolicyLimit({
          current: (error as any).current,
          limit: (error as any).limit,
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create policy record. Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ScopingAILayout>
      <div className="min-h-screen bg-[#FCFBF4]">
        {/* Header */}
        <motion.div
          className="bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-sm"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/revisr/audit-workflow-selection')}
                  className="gap-2 hover:bg-white/50 transition-all duration-300 group mb-2"
                >
                  <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform duration-300" />
                  Back to Workflow Selection
                </Button>
                <h1 className="text-2xl font-bold text-gray-900">Create New Policy</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Enter policy details and select a template for AI-powered generation
                </p>
              </div>
            </div>

            {/* Stepper */}
            <Stepper currentStep={1} steps={CREATE_POLICY_STEPS} />
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {policyLimit && (
            <Card className="border-2 border-orange-200 shadow-sm mb-6">
              <CardContent className="py-12 px-8 flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-orange-100 flex items-center justify-center mb-5">
                  <Lock className="h-8 w-8 text-orange-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Policy Limit Reached</h3>
                <p className="text-gray-500 text-sm mb-6 max-w-sm">
                  You've used all the policies allowed on your current plan. Upgrade to create unlimited policies.
                </p>
                {policyLimit.limit !== undefined && (
                  <div className="w-full max-w-xs bg-white border border-orange-100 rounded-lg p-4 mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Policies used</span>
                      <span className="font-semibold text-gray-800">
                        {policyLimit.current ?? '—'} / {policyLimit.limit}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-orange-400 rounded-full"
                        style={{
                          width: `${Math.min(
                            ((policyLimit.current ?? 0) / policyLimit.limit) * 100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
                <Button variant="outline" onClick={() => navigate('/revisr/dashboard')}>
                  Back to Dashboard
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Policy Details Card */}
              <Card className="border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                <CardHeader className="pb-4 border-b border-gray-100">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2 text-gray-900">
                    <FileText className="h-5 w-5 text-[#33b864]" />
                    Policy Details
                  </CardTitle>
                  <p className="text-xs text-gray-500 mt-1">
                    Provide the basic information for your new policy document
                  </p>
                </CardHeader>
                <CardContent className="space-y-5 pt-6">
                  {/* Policy Name Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2 flex items-center gap-1">
                      Policy Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={policyName}
                      onChange={(e) => setPolicyName(e.target.value)}
                      placeholder="e.g., Workplace Health & Safety Policy"
                      className="w-full h-11 text-base"
                      maxLength={200}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-500">
                        Enter a clear, descriptive name for your policy
                      </p>
                      <p className="text-xs text-gray-400 font-mono">
                        {policyName.length}/200
                      </p>
                    </div>
                  </div>

                  {/* Template Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2 flex items-center gap-1">
                      Template <span className="text-red-500">*</span>
                    </label>
                    <TemplateSelector
                      value={selectedTemplate}
                      onChange={handleTemplateChange}
                      preloadedTemplates={templates}
                      isExternalLoading={isLoadingTemplates}
                    />
                    <p className="text-xs text-gray-500 mt-2 flex items-start gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-[#33b864] flex-shrink-0 mt-0.5" />
                      <span>Select a template from your Revisr Library to guide AI generation</span>
                    </p>
                  </div>

                  {/* Additional Instructions */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Additional Instructions
                      <span className="text-gray-500 font-normal ml-1.5 text-xs">(optional)</span>
                    </label>
                    <Textarea
                      value={additionalInstructions}
                      onChange={(e) => setAdditionalInstructions(e.target.value)}
                      placeholder="Add specific instructions for AI generation, such as:\n• Focus on construction industry safety requirements\n• Include sections on remote work policies\n• Emphasize compliance with ISO standards"
                      className="w-full min-h-[120px] resize-y text-sm"
                      maxLength={2000}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-500">
                        Provide context-specific guidance to customize the generated policy
                      </p>
                      <p className="text-xs text-gray-400 font-mono">
                        {additionalInstructions.length}/2000
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Org Context Card */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-[#33b864]" />
                      Organization Context
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      The AI will incorporate these organization details into your policy document
                    </p>
                  </div>
                </div>
                <OrgContextCard organizationId={orgId} />
              </div>

              {/* Submit Button */}
              <div className="flex flex-col gap-3 pt-4 border-t border-gray-100">
                <Button
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting || !!policyLimit}
                  className="bg-[#33b864] hover:bg-[#2a9d54] text-white px-8 h-12 font-semibold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  size="lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Creating Policy...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 mr-2" />
                      Generate Policy with AI
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </>
                  )}
                </Button>
                {!isFormValid && (
                  <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-md border border-amber-200">
                    <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>
                      Please enter a policy name and select a template to continue
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Template Preview */}
            <div className="lg:col-span-1">
              <div className="sticky top-4">
                <Card className="border border-gray-200 bg-gradient-to-br from-white to-gray-50 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <CardHeader className="pb-3 border-b border-gray-100">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 text-gray-900">
                      <FileText className="h-4 w-4 text-[#33b864]" />
                      Template Preview
                    </CardTitle>
                    <p className="text-xs text-gray-500 mt-1">
                      Preview of your selected template
                    </p>
                  </CardHeader>
                  <CardContent className="pt-4">
                    {selectedTemplate ? (
                      <div>
                        <div className="mb-4 pb-4 border-b border-gray-200 bg-[#33b864]/5 -mx-4 px-4 py-3 rounded-t-lg">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-1">
                              <div className="h-10 w-10 rounded-lg bg-[#33b864]/10 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-[#33b864]" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {selectedTemplate.doc_name}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  {selectedTemplate.chunk_count} chunk{selectedTemplate.chunk_count !== 1 ? 's' : ''}
                                </Badge>
                                {selectedTemplate.category && (
                                  <Badge variant="outline" className="text-xs">
                                    {selectedTemplate.category}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        {templatePreview ? (
                          <div className="max-h-[500px] overflow-y-auto pr-2">
                            <PerfectMarkdownRenderer content={templatePreview} />
                          </div>
                        ) : (
                          <div className="text-center py-12 px-4">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#33b864]/10 mb-3">
                              <Sparkles className="h-8 w-8 text-[#33b864]" />
                            </div>
                            <p className="text-sm font-medium text-gray-700">Template Ready</p>
                            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                              AI will use this template as a structural guide when generating your policy document
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-16 px-4">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
                          <FileText className="h-10 w-10 text-gray-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-600 mb-1">No Template Selected</p>
                        <p className="text-xs text-gray-400 leading-relaxed">
                          Choose a template from the dropdown above to see details here
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </ScopingAILayout>
  );
}
